import { useEffect, useState } from "react";
import {
    ActionIcon,
    Badge,
    Button,
    Group,
    Image,
    Modal,
    Stack,
    Text,
    Title,
    Transition,
    UnstyledButton,
} from "@mantine/core";
import { InfoHint } from "../InfoHint/InfoHint";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import {
    IconArrowLeft,
    IconArrowRight,
    IconCalendar,
    IconCheck,
    IconMinus,
    IconPlus,
} from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import "@mantine/dates/styles.css";
import classes from "./BuyWizard.module.css";
import { useWizard } from "./WizardContext";
import { WizardCheckout } from "./WizardCheckout";
import { useNavigate } from "react-router-dom";
import {
    Edition,
    Duration,
    LMUTV_PRICING,
    formatValidUntil,
    validUntilForDuration,
} from "../../Settings";
import { getLineTotal } from "../PayPalCheckout/UseCart";
import { PAYPAL_SANDBOX } from "../../../config/paypal";
import standardImg from "../../media/Normal.png";
import proImg from "../../media/Pro.png";
import i18n from "../../i18n";

// 1-day plans exist only for renewal testing → offer them only in sandbox builds.
const DURATIONS: Duration[] = PAYPAL_SANDBOX
  ? ["1d", "3m", "1y", "lifetime"]
  : ["3m", "1y", "lifetime"];

// Deferred subscription start is OFF: the date reached PayPal (billing was deferred)
// but never reached the Key API, so the key started immediately and expired before
// the paid period did. Offering a control that silently does nothing is worse than
// not offering it. Flip back to true once the key is issued at the start date —
// see docs/lmutv-subscription-start-date.md.
const START_DATE_ENABLED = false;

export function BuyWizard() {
    const { t } = useTranslation("translation");
    const wizard = useWizard();
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width: 50em)");
    const [step, setStep] = useState<0 | 1 | 2>(0);
    const [edition, setEdition] = useState<Edition | undefined>();
    const [duration, setDuration] = useState<Duration | undefined>();
    const [pcs, setPcs] = useState<number>(1);
    const [startsAt, setStartsAt] = useState<Date>(() => new Date());

    // Reset whenever the wizard re-opens, so closing mid-flow restarts cleanly.
    // A deeplink preselect (from an email link) can seed the tier and jump the
    // user straight to the plan or checkout step.
    useEffect(() => {
        if (wizard?.opened) {
            const p = wizard?.preselect;
            setEdition(p?.edition);
            setDuration(p?.duration);
            setPcs(p?.pcs ?? 1);
            setStartsAt(new Date());
            setStep(p?.edition && p?.duration ? 2 : p?.edition ? 1 : 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wizard?.opened]);

    const startsAtISO = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const pickEdition = (e: Edition) => {
        setEdition(e);
        // Small delay so the user sees the selected highlight before moving on.
        setTimeout(() => setStep(1), 250);
    };

    // Duration chosen → advance to the in-modal checkout (subscribe or one-time).
    const confirm = () => {
        if (!edition || !duration) return;
        setStep(2);
    };

    const finishCheckout = () => {
        wizard?.closeWizard();
        navigate("/account");
    };

    const stepTitle = [
        t("wizard.step.version.title"),
        t("wizard.step.plan.title"),
        t("subscribe.title"),
    ][step];

    const stepHint: string | null = [
        null,
        t("wizard.step.plan.hint"),
        null,
    ][step];

    return (
        <Modal
            opened={!!wizard?.opened}
            onClose={() => wizard?.closeWizard()}
            size="lg"
            centered
            withCloseButton
            fullScreen={isMobile}
            className={classes.modal}
            title={
                step > 0 ? (
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label={t("wizard.back")}
                        onClick={() => setStep((s) => (s > 0 ? ((s - 1) as 0 | 1) : s))}
                    >
                        <IconArrowLeft size={20} />
                    </ActionIcon>
                ) : null
            }
        >
            <Stack gap="md">
                <Group justify="center" gap={6} wrap="nowrap">
                    <Title order={2} ta="center" className={classes.stepTitle}>
                        {stepTitle}
                    </Title>
                    {stepHint && <InfoHint label={stepHint} width={280} />}
                </Group>
                <div className={classes.dots}>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className={`${classes.dot} ${i === step ? classes.dotActive : ""}`}
                        />
                    ))}
                </div>

                <div className={classes.stepContainer}>
                    {/* Step 0 — Version */}
                    <Transition mounted={step === 0} transition="fade" duration={200}>
                        {(s) => (
                            <div style={s} className={classes.stepBody}>
                                <div className={classes.cards}>
                                    {(["standard", "pro"] as Edition[]).map((e) => {
                                        const selected = edition === e;
                                        const isPro = e === "pro";
                                        const monthly = (LMUTV_PRICING[e]["1y"] / 12).toFixed(2);
                                        const lifetime = LMUTV_PRICING[e].lifetime;
                                        const features: string[] =
                                            (t(`wizard.features.${e}`, { returnObjects: true }) as unknown) as string[];
                                        const featureRows = Array.isArray(features) ? features : [];
                                        return (
                                            <UnstyledButton
                                                key={e}
                                                className={`${classes.editionCard} ${
                                                    isPro ? classes.editionCardPro : ""
                                                } ${selected ? classes.editionCardSelected : ""}`}
                                                onClick={() => pickEdition(e)}
                                            >
                                                {isPro && (
                                                    <div className={classes.popularRibbon}>
                                                        {t("wizard.mostPopular")}
                                                    </div>
                                                )}
                                                {selected && (
                                                    <div className={classes.selectedBadge}>
                                                        <IconCheck size={16} stroke={3} />
                                                    </div>
                                                )}
                                                <Image
                                                    src={isPro ? proImg : standardImg}
                                                    alt={t(`cta.main.costs.cost${isPro ? 2 : 1}.name`)}
                                                    className={classes.editionImage}
                                                />
                                                <Text fw={800} size="xl" className={classes.editionName}>
                                                    {t(`cta.main.costs.cost${isPro ? 2 : 1}.name`)}
                                                </Text>
                                                <div className={classes.priceAnchor}>
                                                    <Text component="span" size="xs" c="dimmed">
                                                        {t("wizard.priceFromLabel")}
                                                    </Text>
                                                    <Text component="span" className={classes.priceBig}>
                                                        {monthly}€
                                                    </Text>
                                                    <Text component="span" size="xs" c="dimmed">
                                                        {t("wizard.perMonth")}
                                                    </Text>
                                                </div>
                                                <Text size="xs" c="dimmed" mb="xs">
                                                    {t("wizard.orLifetime", { price: lifetime })}
                                                </Text>
                                                <div className={classes.featureList}>
                                                    {isPro && (
                                                        <Text size="xs" fw={700} c="lmuTVColors.4" mb={6}>
                                                            {t("wizard.everythingInStandard")}
                                                        </Text>
                                                    )}
                                                    {featureRows.map((f, i) => (
                                                        <div key={i} className={classes.featureRow}>
                                                            <IconCheck
                                                                size={14}
                                                                stroke={3}
                                                                color={isPro ? "#dc3d31" : "#9aa0a6"}
                                                            />
                                                            <Text size="sm">{f}</Text>
                                                        </div>
                                                    ))}
                                                </div>
                                            </UnstyledButton>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </Transition>

                    {/* Step 1 — PCs + Duration (price on the cards updates live) */}
                    <Transition mounted={step === 1} transition="fade" duration={200}>
                        {(s) => (
                            <div style={s} className={classes.stepBody}>
                              <Stack w="100%" gap="lg">
                                {/* PC counter — the duration totals below react to it */}
                                <Stack align="center" gap="xs">
                                    <Text size="sm" fw={700} c="dimmed" tt="uppercase">
                                        {t("cart.pcs")}
                                    </Text>
                                    <div className={classes.counter}>
                                        <ActionIcon
                                            size={48}
                                            radius="xl"
                                            variant="filled"
                                            color="lmuTVColors"
                                            aria-label="decrement"
                                            disabled={pcs <= 1}
                                            onClick={() => setPcs((p) => Math.max(1, p - 1))}
                                        >
                                            <IconMinus size={22} />
                                        </ActionIcon>
                                        <div className={classes.counterNum}>{pcs}</div>
                                        <ActionIcon
                                            size={48}
                                            radius="xl"
                                            variant="filled"
                                            color="lmuTVColors"
                                            aria-label="increment"
                                            disabled={pcs >= 50}
                                            onClick={() => setPcs((p) => Math.min(50, p + 1))}
                                        >
                                            <IconPlus size={22} />
                                        </ActionIcon>
                                    </div>
                                    {/* Reserved slot so toggling bulk doesn't shift the cards. */}
                                    <Text
                                        c="lmuTVColors.4"
                                        size="sm"
                                        fw={500}
                                        style={{
                                            visibility: pcs > 3 ? "visible" : "hidden",
                                            transition: "opacity 200ms ease",
                                            opacity: pcs > 3 ? 1 : 0,
                                        }}
                                    >
                                        {t("cart.bulkNote")}
                                    </Text>
                                </Stack>

                                {/* Duration — totals reflect the PC count above */}
                                <Stack w="100%" gap="md">
                                    {DURATIONS.map((d) => {
                                        const selected = duration === d;
                                        const total = edition
                                            ? getLineTotal({ edition, duration: d, pcs })
                                            : LMUTV_PRICING.standard[d];
                                        // startsAt applies to *all* non-lifetime cards so the user can
                                        // see the resulting expiry date update across the board. With the
                                        // picker disabled, startsAt is always today — which is exactly
                                        // when the key really starts.
                                        const fromDate =
                                            START_DATE_ENABLED && d !== "lifetime" ? startsAt : new Date();
                                        const validity =
                                            d === "lifetime"
                                                ? t("cart.validForever")
                                                : `${t("cart.validUntil")}: ${formatValidUntil(
                                                      validUntilForDuration(d, fromDate),
                                                      i18n.language
                                                  )}`;
                                        return (
                                            <UnstyledButton
                                                key={d}
                                                className={`${classes.durationCard} ${
                                                    selected ? classes.durationCardSelected : ""
                                                }`}
                                                onClick={() => setDuration(d)}
                                            >
                                                <Stack gap={2} align="flex-start">
                                                    <Group gap={6} align="center">
                                                        <Text fw={700} size="lg">
                                                            {t(`cart.durations.${d}`)}
                                                        </Text>
                                                        {START_DATE_ENABLED && d !== "lifetime" && (
                                                            <Badge
                                                                size="xs"
                                                                variant="light"
                                                                color="lmuTVColors"
                                                                leftSection={<IconCalendar size={10} />}
                                                            >
                                                                {t("wizard.customStartBadge")}
                                                            </Badge>
                                                        )}
                                                    </Group>
                                                    <Text size="xs" c="dimmed">
                                                        {validity}
                                                    </Text>
                                                </Stack>
                                                <Text fw={700} size="xl" c="lmuTVColors.4">
                                                    {total.toFixed(2)}€
                                                </Text>
                                            </UnstyledButton>
                                        );
                                    })}
                                    {/* Reserved slot so picking 3m/1y vs lifetime doesn't shift the cards. */}
                                    {START_DATE_ENABLED && (
                                        <div style={{ minHeight: 80 }}>
                                            {duration && duration !== "lifetime" && (
                                                <DateInput
                                                    size="sm"
                                                    label={t("cart.startsAt")}
                                                    description={t("cart.startsAtHint")}
                                                    leftSection={<IconCalendar size={16} />}
                                                    value={startsAt}
                                                    minDate={new Date()}
                                                    onChange={(d: any) => {
                                                        if (!d) return;
                                                        setStartsAt(d instanceof Date ? d : new Date(d));
                                                    }}
                                                    valueFormat="DD.MM.YYYY"
                                                />
                                            )}
                                        </div>
                                    )}
                                </Stack>
                              </Stack>
                            </div>
                        )}
                    </Transition>

                    {/* Step 2 — Checkout (subscribe or one-time, in the modal) */}
                    <Transition mounted={step === 2} transition="fade" duration={200}>
                        {(s) => (
                            <div style={s} className={classes.stepBody}>
                                {edition && duration && (
                                    <WizardCheckout
                                        edition={edition}
                                        duration={duration}
                                        pcs={pcs}
                                        startsAt={
                                            !START_DATE_ENABLED || duration === "lifetime"
                                                ? undefined
                                                : startsAtISO(startsAt)
                                        }
                                        upgradeKeyIdent={wizard?.preselect?.upgradeKeyIdent}
                                        onDone={finishCheckout}
                                    />
                                )}
                            </div>
                        )}
                    </Transition>
                </div>

                {/* Footer nav */}
                <Group justify="space-between" pt="sm">
                    <div />
                    {step === 1 && (
                        <Button
                            rightSection={<IconArrowRight size={16} />}
                            onClick={confirm}
                            disabled={!duration}
                            color="lmuTVColors"
                        >
                            {t("wizard.next")}
                        </Button>
                    )}
                </Group>
            </Stack>
        </Modal>
    );
}
